import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ActivePlatformBroadcast } from '@accestra/types';
import { api, friendlyError } from '../../lib/api-client';
import { useAuth } from '../../hooks/useAuth';
import AlertBanner, { type AlertVariant } from '../ui/AlertBanner';
import Button from '../ui/Button';

interface ActiveBroadcastResponse {
  broadcasts: ActivePlatformBroadcast[];
}

function severityVariant(severity: ActivePlatformBroadcast['severity']): AlertVariant {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'success':
      return 'success';
    default:
      return 'info';
  }
}

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//iu.test(url);
}

export default function AppBroadcastStack() {
  const { t, i18n } = useTranslation(['broadcasts', 'common']);
  const { user, org } = useAuth();
  const [broadcasts, setBroadcasts] = useState<ActivePlatformBroadcast[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const loadBroadcasts = useCallback(async () => {
    if (!user) {
      setBroadcasts([]);
      return;
    }

    setError(null);
    try {
      const response = await api.get<ActiveBroadcastResponse>('/broadcasts/active');
      setBroadcasts(response.broadcasts);
    } catch (nextError) {
      setError(friendlyError(nextError, t('broadcasts:errors.loadFailed')));
    }
  }, [t, user]);

  useEffect(() => {
    void loadBroadcasts();

    if (!user) {
      return undefined;
    }

    const timer = window.setInterval(() => void loadBroadcasts(), 60_000);
    return () => window.clearInterval(timer);
  }, [loadBroadcasts, i18n.resolvedLanguage, org?.id, user]);

  const hasBroadcasts = broadcasts.length > 0;
  const showContainer = hasBroadcasts || error !== null;
  const ariaLabel = useMemo(
    () => t('broadcasts:banner.ariaLabel'),
    [t],
  );

  async function dismissBroadcast(id: string) {
    setDismissingId(id);
    setError(null);
    try {
      await api.post<null>(`/broadcasts/${id}/dismiss`);
      setBroadcasts((current) => current.filter((broadcast) => broadcast.id !== id));
    } catch (nextError) {
      setError(friendlyError(nextError, t('broadcasts:errors.dismissFailed')));
    } finally {
      setDismissingId(null);
    }
  }

  if (!showContainer) {
    return null;
  }

  return (
    <section className="acc-broadcast-stack" aria-label={ariaLabel}>
      {error ? (
        <AlertBanner variant="warning" role="status">
          <div className="acc-broadcast-stack__error">
            <div>{error}</div>
            <Button variant="secondary" size="sm" onClick={() => void loadBroadcasts()}>
              {t('common:actions.refresh')}
            </Button>
          </div>
        </AlertBanner>
      ) : null}

      {broadcasts.map((broadcast) => (
        <AlertBanner
          key={`${broadcast.id}:${broadcast.version}`}
          variant={severityVariant(broadcast.severity)}
        >
          <div className="acc-broadcast-stack__item">
            <div className="acc-broadcast-stack__copy">
              <div className="acc-broadcast-stack__title">
                {broadcast.content.title}
              </div>
              <div className="acc-broadcast-stack__body">
                {broadcast.content.body}
              </div>
            </div>

            <div className="acc-broadcast-stack__actions">
              {broadcast.content.ctaLabel && broadcast.ctaUrl ? (
                isExternalUrl(broadcast.ctaUrl) ? (
                  <a
                    href={broadcast.ctaUrl}
                    className="acc-broadcast-stack__cta"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {broadcast.content.ctaLabel}
                  </a>
                ) : (
                  <Link to={broadcast.ctaUrl} className="acc-broadcast-stack__cta">
                    {broadcast.content.ctaLabel}
                  </Link>
                )
              ) : null}

              {broadcast.isDismissible ? (
                <button
                  type="button"
                  className="acc-broadcast-stack__dismiss"
                  disabled={dismissingId === broadcast.id}
                  onClick={() => void dismissBroadcast(broadcast.id)}
                >
                  {dismissingId === broadcast.id
                    ? t('broadcasts:actions.dismissing')
                    : t('broadcasts:actions.dismiss')}
                </button>
              ) : (
                <span className="acc-broadcast-stack__persistent">
                  {t('broadcasts:banner.persistent')}
                </span>
              )}
            </div>
          </div>
        </AlertBanner>
      ))}
    </section>
  );
}
